import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-mui';

import StyledTextField from '../InputTextField';

/**
 * Field configuration for login credentials
 */
export interface FieldConfig {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}

/**
 * Default field configurations
 */
const DEFAULT_API_KEY_FIELD: FieldConfig = {
  label: 'Enter API key',
  name: 'apikey',
  type: 'text',
  required: true,
};

const DEFAULT_USERNAME_FIELD: FieldConfig = {
  label: 'Admin Username',
  name: 'username',
  type: 'text',
  required: true,
};

const DEFAULT_PASSWORD_FIELD: FieldConfig = {
  label: 'Admin Password',
  name: 'password',
  type: 'password',
  required: true,
};

/**
 * Props for the LoginCreds component
 */
interface LoginCredsProps {
  disabled?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  fields?: FieldConfig[];
  // Callback triggered when field values change
  onFieldChange?: { [fieldName: string]: (value: string) => void };
}

/**
 * A flexible login credentials component that handles both API key and username/password cases
 * - If 1 field is provided, defaults to API key
 * - If 2 fields are provided, defaults to username/password
 * - Any fields can be overridden by providing custom field configs
 */
export default function LoginCreds({
  disabled = false,
  onValidityChange,
  fields,
  onFieldChange,
}: LoginCredsProps) {
  const { unregister, resetField, register, watch } = useFormContext();

  // Determine field configuration based on the number of fields provided
  const fieldsConfig = useMemo(() => {
    let config: FieldConfig[] = fields || [];

    // Apply defaults if no fields provided
    if (config.length === 0) {
      config = [DEFAULT_USERNAME_FIELD, DEFAULT_PASSWORD_FIELD];
    } else if (config.length === 1 && !fields) {
      // If exactly one field is in the default config (not explicitly provided),
      // assume it's an API key field
      config = [DEFAULT_API_KEY_FIELD];
    }

    // Ensure all fields have a type
    return config.map((field) => ({
      type: 'text',
      required: true,
      ...field,
    }));
  }, [fields]);

  // Watch all configured fields
  const fieldValues = fieldsConfig.map((field) => ({
    name: field.name,
    value: watch(field.name),
    required: field.required !== false,
  }));

  // Form is valid when all required fields have values
  const isValid =
    !disabled &&
    fieldValues.every(
      (field) =>
        !field.required ||
        (!!field.value && field.value.toString().trim() !== '')
    );

  // Report validity status to parent component
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // Handle field registration and unregistration based on disabled state
  useEffect(() => {
    if (disabled) {
      // First unregister to remove from validation
      unregister(fieldsConfig.map((f) => f.name));

      // Then reset fields to clear any error states
      fieldsConfig.forEach((field) => {
        resetField(field.name, {
          defaultValue: '',
          keepError: false,
          keepDirty: false,
          keepTouched: false,
        });
      });

      // Ensure validity is updated
      onValidityChange?.(false);
    } else {
      // Re-register fields when enabled
      fieldsConfig.forEach((field) => {
        register(field.name, { required: field.required !== false });
      });
    }
  }, [
    disabled,
    unregister,
    resetField,
    register,
    onValidityChange,
    fieldsConfig,
  ]);

  // Handle field change events
  const handleFieldChange = (fieldName: string, value: string) => {
    if (onFieldChange && onFieldChange[fieldName]) {
      onFieldChange[fieldName](value);
    }
  };

  return (
    <>
      {fieldsConfig.map((field, index) => (
        <StyledTextField
          key={`${field.name}-${index}`}
          label={field.label}
          type={field.type}
          name={field.name}
          required={field.required !== false && !disabled}
          disabled={disabled}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
        />
      ))}
    </>
  );
}
